import { Stack, Typography } from '@mui/material';
import { SchoolPropertyCategory } from '@schooly/api';
import { EditIcon, PlusIcon } from '@schooly/style';
import React, { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form-lts';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { SimpleButton } from '../../../../../components/uikit/SimpleButton/SimpleButton';
import { ModalPanel } from '../../../../../components/uikit-components/Modal/Modal.styled';
import useQueryStringParams from '../../../../../hooks/useQueryStringParams';
import {
  SchoolTuneStatusesArchivable,
  SchoolTuneStatusesForm,
  SchoolTuneStatusModalMode,
} from './scheme';
import { SchoolTuneStatusesModalMainPanel } from './SchoolTuneStatusesModalMainPanel';

export interface SchoolTuneStatusesModalStudentsPanelProps extends SchoolTuneStatusesArchivable {
  categories: SchoolPropertyCategory[];
}

export const SchoolTuneStatusesModalStaffPanel: FC<SchoolTuneStatusesModalStudentsPanelProps> = ({
  showArchived,
  setShowArchived,
  autoSwitchedArchivedOn,
  categories,
}) => {
  const navigate = useNavigate();
  const { type } = useQueryStringParams<'type'>();
  const { watch } = useFormContext<SchoolTuneStatusesForm>();

  const isActive = type === SchoolTuneStatusModalMode.STAFF;
  const statuses = watch('staff');

  const hasStatuses = useMemo(() => {
    const categoriesWithStatuses = new Set(statuses.map((status) => status.category?.name));
    return categories.every((category) => categoriesWithStatuses.has(category.name));
  }, [categories, statuses]);

  const handlePanelClick = useCallback(() => {
    navigate({ search: `?type=${SchoolTuneStatusModalMode.STAFF}` }, { replace: true });
  }, [navigate]);

  return (
    <>
      <ModalPanel active={isActive} withBorderTop>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h2">
            <FormattedMessage id="school-tabs-Statuses-StaffStatuses" />
          </Typography>
          {!isActive &&
            (hasStatuses ? (
              <SimpleButton startIcon={<EditIcon />} onClick={handlePanelClick}>
                <FormattedMessage id="school-tabs-Statuses-EditStatuses" />
              </SimpleButton>
            ) : (
              <SimpleButton startIcon={<PlusIcon />} onClick={handlePanelClick}>
                <FormattedMessage id="school-tabs-Statuses-AddStatus" />
              </SimpleButton>
            ))}
        </Stack>
      </ModalPanel>
      {isActive && (
        <SchoolTuneStatusesModalMainPanel
          type={SchoolTuneStatusModalMode.STAFF}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
          autoSwitchedArchivedOn={autoSwitchedArchivedOn}
          categories={categories}
        />
      )}
    </>
  );
};
